import { cloneElement, ReactElement, useId } from 'react';
import { Props as SelectProps } from 'chakra-react-select';
import { Control, Controller, FieldError, FieldErrorsImpl, FieldValues, Merge, Path } from 'react-hook-form';

import { FormControl, FormErrorMessage, FormHelperText, FormLabel } from '@chakra-ui/react';
import { StyleProps } from '@chakra-ui/styled-system';
import { WarningIcon } from '@udacity/chakra-uds-icons';

import { SelectOption } from '~/types/select-option';

import { SelectInput } from './select-input';

export function FormSelectInput<T extends FieldValues>({
  label,
  name,
  options,
  placeholder,
  helperText,
  errorMessage,
  control,
  selectProps,
  isCreatable = false,
  openMenuOnFocus,
  isRequired = false,
  containerStyles,
  controlBorderColor,
}: {
  label: string;
  name: Path<T>;
  options: SelectOption[];
  placeholder: string;
  helperText?: string;
  errorMessage?: string | FieldError | Merge<FieldError, FieldErrorsImpl<T>> | undefined;
  control?: Control<T, object>;
  selectProps?: SelectProps;
  isCreatable?: boolean;
  openMenuOnFocus?: boolean;
  isRequired?: boolean;
  containerStyles?: StyleProps;
  controlBorderColor?: string;
}) {
  const controlId = useId();

  return (
    <FormControl id={controlId} isInvalid={Boolean(errorMessage)} {...containerStyles}>
      <FormLabel mb={helperText ? 0 : '8px'} data-peer>
        {label}
      </FormLabel>

      {helperText && <FormHelperText margin='0 0 8px 0'>{helperText}</FormHelperText>}
      <WithControl control={control} name={name} selectProps={selectProps}>
        <SelectInput
          controlBorderColor={controlBorderColor}
          controlId={controlId}
          isCreatable={isCreatable}
          isRequired={isRequired}
          name={name}
          openMenuOnFocus={openMenuOnFocus}
          options={options}
          placeholder={placeholder}
          selectProps={selectProps}
        />
      </WithControl>
      {errorMessage && (
        <FormErrorMessage mt='8px'>
          <>
            <WarningIcon h='26px' w='26px' />
            {errorMessage}
          </>
        </FormErrorMessage>
      )}
    </FormControl>
  );
}

function WithControl({
  control,
  name,
  children,
  selectProps,
}: {
  control?: any;
  name: string;
  children: ReactElement;
  selectProps?: SelectProps;
}) {
  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          return cloneElement(children, { selectProps: { ...selectProps, ...field } });
        }}
      />
    );
  } else {
    return children;
  }
}
